/**
 * usePushSubscription — V1.7.0 Sprint 1.
 *
 * Provides:
 *   - `permission`    — "default" | "granted" | "denied" | "unsupported"
 *   - `subscribed`    — boolean (are we registered with backend?)
 *   - `requestPermission()` → boolean granted
 *   - `unsubscribe()` → removes from backend + browser
 *
 * The permission request should be triggered CONTEXTUALLY — e.g. after
 * a candidate sends their first DaddyTalk message — never on page load.
 * That single UX rule is the difference between 40% acceptance and 5%.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
    return arr;
}

function isPushSupported() {
    return (
        typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager" in window
        && "Notification" in window
    );
}

export default function usePushSubscription(user) {
    const [permission, setPermission] = useState(
        isPushSupported() ? Notification.permission : "unsupported",
    );
    const [subscribed, setSubscribed] = useState(false);

    // Check current subscription status when user changes.
    useEffect(() => {
        if (!isPushSupported() || !user?.id) return;
        (async () => {
            try {
                const reg = await navigator.serviceWorker.ready;
                const sub = await reg.pushManager.getSubscription();
                setSubscribed(!!sub);
            } catch {
                setSubscribed(false);
            }
        })();
    }, [user?.id]);

    const subscribeWithBackend = useCallback(async (sub) => {
        const raw = sub.toJSON();
        await api.post("/me/push/subscribe", {
            endpoint: raw.endpoint,
            keys: raw.keys,
            user_agent: navigator.userAgent.slice(0, 200),
            platform: window.matchMedia("(display-mode: standalone)").matches ? "pwa" : "web",
        });
    }, []);

    const requestPermission = useCallback(async () => {
        if (!isPushSupported()) return false;
        try {
            const perm = await Notification.requestPermission();
            setPermission(perm);
            if (perm !== "granted") return false;

            const { data: vapidData } = await api.get("/me/push/vapid-public-key");
            if (!vapidData?.key) {
                console.warn("VAPID public key not configured on backend");
                return false;
            }
            const reg = await navigator.serviceWorker.ready;
            const existing = await reg.pushManager.getSubscription();
            const sub = existing || await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidData.key),
            });
            await subscribeWithBackend(sub);
            setSubscribed(true);
            return true;
        } catch (err) {
            console.warn("push permission flow failed:", err);
            return false;
        }
    }, [subscribeWithBackend]);

    const unsubscribe = useCallback(async () => {
        if (!isPushSupported()) return;
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                const endpoint = sub.endpoint;
                await sub.unsubscribe();
                try { await api.post("/me/push/unsubscribe", { endpoint }); } catch { /* ignore */ }
            }
            setSubscribed(false);
        } catch (err) {
            console.warn("unsubscribe failed:", err);
        }
    }, []);

    return {
        supported: isPushSupported(),
        permission,
        subscribed,
        requestPermission,
        unsubscribe,
    };
}
